import { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { motion, useScroll, useTransform } from "framer-motion";
import { ArrowUpRight, Pause, Play } from "lucide-react";
import HeroGeometry from "../components/HeroGeometry";
import { MaskLines, FadeUp, Rule } from "../components/Reveal";
import Marquee from "../components/Marquee";
import FragmentMachine from "../components/FragmentMachine";
import DailyFragment from "../components/DailyFragment";
import SubscribeForm from "../components/SubscribeForm";
import { useAudio } from "../context/AudioContext";
import { api, asList, fmt } from "../lib/api";
import useSeo from "../lib/useSeo";

function Hero() {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end start"] });
  const gy = useTransform(scrollYProgress, [0, 1], [0, 180]);
  const ty = useTransform(scrollYProgress, [0, 1], [0, -80]);
  return (
    <section ref={ref} className="relative min-h-[100svh] grid-bg overflow-hidden px-5 sm:px-8 lg:px-12 pt-28 pb-16" data-testid="home-hero">
      <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_70%_40%,transparent_0%,var(--ink)_70%)]" />
      <div className="relative grid grid-cols-1 lg:grid-cols-12 gap-10 items-center min-h-[calc(100svh-11rem)]">
        <motion.div style={{ y: ty }} className="lg:col-span-7 order-2 lg:order-1 z-10">
          <p className="eyebrow mb-6 flex gap-4"><span>N&deg; 001</span><span>Field work, ongoing</span></p>
          <h1 className="f-display font-black uppercase leading-[0.82] text-[19vw] sm:text-[15vw] lg:text-[10.5vw] tracking-tight">
            <MaskLines onMount delay={0.15} lines={["Outlaw", <span key="c" className="outline-text">Creative</span>]} />
          </h1>
          <div className="mt-6 pl-1 sm:pl-2">
            <MaskLines onMount delay={0.55} lines={["(the geometry of life)"]} lineClass="f-serif italic font-light text-3xl sm:text-5xl text-[var(--bone)]" />
          </div>
          <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 1.3, duration: 1 }} className="mt-12 flex flex-wrap gap-x-8 gap-y-4">
            {[["/explore", "Explore", "Read"], ["/listen", "Listen", "Hear"], ["/subscribe", "Subscribe", "Stay"]].map(([to, l, v], i) => (
              <Link key={to} to={to} className="group flex items-baseline gap-3 border-b border-[var(--line)] hover:border-[var(--signal)] pb-2 transition-colors duration-500" data-testid={`hero-${l.toLowerCase()}-cta`}>
                <span className="f-mono text-[11px] text-[var(--ash)]">0{i + 1}</span>
                <span className="f-display font-bold uppercase text-2xl tracking-wide">{l}</span>
                <span className="f-serif italic text-[var(--ash)] text-sm hidden sm:inline">/ {v}</span>
                <ArrowUpRight size={16} className="self-center transition-transform duration-500 group-hover:-translate-y-0.5 group-hover:translate-x-0.5 group-hover:text-[var(--signal)]" />
              </Link>
            ))}
          </motion.div>
        </motion.div>
        <div className="lg:col-span-5 order-1 lg:order-2 flex justify-center lg:justify-end">
          <HeroGeometry y={gy} />
        </div>
      </div>
      <div className="relative mt-10 flex justify-between f-mono text-[11px] text-[var(--ash)]">
        <span>x 37.0 / y 61.8</span>
        <span className="hidden sm:inline">Stay a minute. Look around. Trip over something new.</span>
        <span>scroll &darr;</span>
      </div>
    </section>
  );
}

const MANIFESTO = ["This is not an introduction.", "This is a filter.", "This is not content.", "This is field work.", "Proceed accordingly."];

function Manifesto() {
  return (
    <section className="px-5 sm:px-8 lg:px-12 py-28 lg:py-44 grid grid-cols-1 md:grid-cols-12 gap-10" data-testid="home-manifesto">
      <div className="md:col-span-3">
        <p className="eyebrow">&sect; Premise</p>
        <svg viewBox="0 0 120 120" className="w-24 mt-8 text-[var(--ash)]" aria-hidden="true">
          <circle cx="60" cy="60" r="52" fill="none" stroke="currentColor" strokeWidth="0.8" />
          <path d="M10 70 C 30 40, 50 95, 70 60 S 105 40, 112 58" fill="none" stroke="var(--bone)" strokeWidth="1.6" strokeLinecap="round" />
          <circle cx="60" cy="60" r="2.5" fill="var(--signal)" />
        </svg>
      </div>
      <h2 className="md:col-span-9 f-serif font-light text-4xl sm:text-6xl lg:text-7xl leading-[1.04] tracking-tight">
        <MaskLines lines={MANIFESTO.map((l, i) => (i % 2 ? <em key={l} className="italic text-[var(--ash)]">{l}</em> : l))} />
      </h2>
      <FadeUp className="md:col-start-4 md:col-span-6 mt-6">
        <p className="text-lg sm:text-xl leading-relaxed text-[#cfcec8]">
          Fragments, essays, songs and spoken readings on moral geometry, myth-tech, and the long, hard home. If it resonates, stay. If not, move on.
        </p>
      </FadeUp>
    </section>
  );
}

function Triptych() {
  const { tracks, current, playing, toggle } = useAudio();
  const sl = tracks.filter((t) => t.title === "Sound & Light");
  return (
    <section className="px-5 sm:px-8 lg:px-12 py-24 lg:py-32 border-t border-[var(--line)]" data-testid="home-listen-feature">
      <div className="flex flex-col md:flex-row md:items-end justify-between gap-6 mb-14">
        <div>
          <p className="eyebrow mb-4">02 / Listen &mdash; one idea, three angles</p>
          <h2 className="f-display font-black uppercase text-6xl sm:text-8xl lg:text-9xl leading-[0.85]"><MaskLines lines={["Sound", "& Light"]} /></h2>
        </div>
        <p className="f-serif italic text-2xl sm:text-3xl max-w-md text-[#cfcec8]">&ldquo;The lies that blind us are the ties that bind us.&rdquo;</p>
      </div>
      <div className="grid grid-cols-1 md:grid-cols-3 gap-px bg-[var(--line)] border border-[var(--line)]">
        {sl.map((t, i) => {
          const on = current?.slug === t.slug && playing;
          return (
            <FadeUp key={t.slug} delay={i * 0.12} className="bg-[var(--ink)] group">
              <button onClick={() => toggle(t)} className="w-full text-left p-5 sm:p-6" data-testid={`home-track-${t.slug}`}>
                <div className="relative aspect-[4/5] overflow-hidden">
                  <img src={t.cover} alt={`${t.title}: ${t.version}`} className="w-full h-full object-cover grayscale contrast-125 scale-105 group-hover:scale-100 transition-transform duration-[1.4s] ease-out" />
                  <div className="absolute inset-0 bg-gradient-to-t from-[var(--ink)]/80 via-transparent" />
                  <span className={`absolute bottom-4 left-4 w-14 h-14 rounded-full border border-[var(--bone)] flex items-center justify-center transition-colors duration-300 ${on ? "bg-[var(--signal)] border-[var(--signal)]" : "group-hover:bg-[var(--bone)] group-hover:text-[var(--ink)]"}`}>
                    {on ? <Pause size={18} /> : <Play size={18} className="ml-0.5" />}
                  </span>
                  <span className="absolute top-4 right-4 f-mono text-[11px] text-[var(--bone)]">{fmt(t.duration)}</span>
                </div>
                <p className="f-mono text-[11px] text-[var(--ash)] mt-5">0{i + 1} &middot; {t.kind}</p>
                <p className="f-display font-bold uppercase text-2xl mt-1">{t.version}</p>
              </button>
            </FadeUp>
          );
        })}
      </div>
      <Link to="/listen" className="inline-flex items-center gap-2 mt-10 f-mono text-xs uppercase tracking-[0.2em] link-u" data-testid="home-listen-all-link">Enter the listening room <ArrowUpRight size={14} /></Link>
    </section>
  );
}

function Writing() {
  const [pieces, setPieces] = useState([]);
  useEffect(() => { api.get("/pieces").then((r) => setPieces(asList(r))).catch(() => setPieces([])); }, []);
  const feat = pieces.filter((p) => p.featured).slice(0, 5);
  const [lead, ...rest] = feat;
  return (
    <section className="px-5 sm:px-8 lg:px-12 py-24 lg:py-32 border-t border-[var(--line)]" data-testid="home-explore-feature">
      <p className="eyebrow mb-4">01 / Explore &mdash; selected writing</p>
      <h2 className="f-display font-black uppercase text-6xl sm:text-8xl lg:text-9xl leading-[0.85] mb-16"><MaskLines lines={["Written", <span key="w" className="outline-text">in the field</span>]} /></h2>
      {lead && (
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 lg:gap-16">
          <FadeUp className="lg:col-span-6">
            <Link to={`/explore/${lead.slug}`} className="group block" data-testid={`home-piece-${lead.slug}`}>
              <div className="relative aspect-[4/3] overflow-hidden" style={{ clipPath: "polygon(0 0, 100% 0, 100% 88%, 88% 100%, 0 100%)" }}>
                <img src="/media/ink.jpg" alt="Ink and charcoal texture" className="w-full h-full object-cover grayscale contrast-125 group-hover:scale-105 transition-transform duration-[1.4s]" />
                <svg viewBox="0 0 100 75" className="absolute inset-0 w-full h-full" preserveAspectRatio="none" aria-hidden="true">
                  <circle cx="50" cy="37.5" r="26" fill="none" stroke="var(--bone)" strokeWidth="0.2" vectorEffect="non-scaling-stroke" />
                  <line x1="0" y1="37.5" x2="100" y2="37.5" stroke="var(--bone)" strokeWidth="0.2" vectorEffect="non-scaling-stroke" />
                </svg>
                <span className="absolute top-4 left-4 f-mono text-[11px] bg-[var(--ink)] px-2 py-1">{lead.kind}</span>
              </div>
              <h3 className="f-display font-black uppercase text-5xl sm:text-6xl mt-8 leading-[0.9] group-hover:text-[var(--signal)] transition-colors duration-500">{lead.title}</h3>
              <p className="f-serif italic text-xl text-[#cfcec8] mt-4 max-w-lg">{lead.dek}</p>
            </Link>
          </FadeUp>
          <ol className="lg:col-span-6 border-t border-[var(--line)]">
            {rest.map((p, i) => (
              <FadeUp as="li" key={p.slug} delay={i * 0.08} className="border-b border-[var(--line)]">
                <Link to={`/explore/${p.slug}`} className="group grid grid-cols-12 gap-3 py-7 items-baseline" data-testid={`home-piece-${p.slug}`}>
                  <span className="col-span-2 f-mono text-xs text-[var(--ash)]">{String(i + 2).padStart(2, "0")}</span>
                  <span className="col-span-10 sm:col-span-7">
                    <span className="block f-display font-bold uppercase text-3xl leading-none group-hover:translate-x-2 transition-transform duration-500">{p.title}</span>
                    <span className="block f-serif italic text-[#bdbcb5] mt-2">{p.dek}</span>
                  </span>
                  <span className="hidden sm:block col-span-3 text-right f-mono text-[11px] text-[var(--ash)] uppercase">{p.kind}</span>
                </Link>
              </FadeUp>
            ))}
          </ol>
        </div>
      )}
      <Link to="/explore" className="inline-flex items-center gap-2 mt-12 f-mono text-xs uppercase tracking-[0.2em] link-u" data-testid="home-explore-all-link">The full index <ArrowUpRight size={14} /></Link>
    </section>
  );
}

function Begin() {
  return (
    <section className="relative px-5 sm:px-8 lg:px-12 py-28 lg:py-44 overflow-hidden" data-testid="home-subscribe">
      <img src="/media/sand.jpg" alt="" className="absolute inset-0 w-full h-full object-cover grayscale opacity-25" />
      <div className="absolute inset-0 bg-gradient-to-b from-[var(--ink)] via-transparent to-[var(--ink)]" />
      <div className="relative grid grid-cols-1 lg:grid-cols-12 gap-10">
        <div className="lg:col-span-7">
          <p className="eyebrow mb-6">03 / Subscribe</p>
          <h2 className="f-serif font-light text-5xl sm:text-7xl lg:text-8xl leading-[0.95] tracking-tight">
            <MaskLines lines={["If you are ready \u2014", <em key="b" className="italic">then, begin.</em>]} />
          </h2>
        </div>
        <FadeUp className="lg:col-span-5 lg:pt-24">
          <p className="text-lg text-[#cfcec8] mb-8 leading-relaxed">New essays, songs and spoken readings, sent as they leave the field. No noise. Leave whenever you like.</p>
          <SubscribeForm id="home-subscribe" />
        </FadeUp>
      </div>
      <Rule className="relative mt-24" />
    </section>
  );
}

export default function Home() {
  useSeo(null, "Outlaw Creative (the geometry of life). Fragments, essays, songs and spoken readings on moral geometry, myth-tech and the long, hard home.");
  return (
    <div data-testid="home-page">
      <Hero />
      <Manifesto />
      <DailyFragment />
      <Marquee />
      <FragmentMachine />
      <Writing />
      <Triptych />
      <Begin />
    </div>
  );
}
